import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Plus, X, Check, ChevronsUpDown, Building2, FileText } from 'lucide-react';
import type { TileRowConfig, TileConfig, TileMetric } from './types';
import { TILE_METRIC_METADATA } from './types';
import type { SetDefinition } from '@/components/reports/set/types';
import { setDefinitionToAnalyticsRequest } from '@/components/reports/set/setUtils';
import { useAnalyticsSummary } from '@/hooks/useAnalyticsSummary';
import { calculateTileSummaryMetric } from './tileSummaryMetrics';
import { cn } from '@/lib/utils';

interface TileConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialConfig?: TileRowConfig;
  onSave: (config: TileRowConfig) => void;
  setDefinition?: SetDefinition;
}

const MAX_TILES = 5;

// Metric Selector Component
interface MetricSelectorProps {
  value: TileMetric;
  onValueChange: (value: TileMetric) => void;
  id?: string;
}

const MetricSelector: React.FC<MetricSelectorProps> = ({ value, onValueChange, id }) => {
  const [open, setOpen] = useState(false);

  const allMetrics = [...COMPANY_METRICS, ...PAGE_METRICS];
  const selectedMetric = allMetrics.find(m => m.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-white font-normal"
        >
          <span className="truncate">{selectedMetric?.label || "Select metric..."}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search metrics..." className="h-9" />
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty>No metrics found.</CommandEmpty>

            {/* Company Metrics Group */}
            <CommandGroup heading="Company Metrics">
              {COMPANY_METRICS.map((metric) => (
                <CommandItem
                  key={metric.value}
                  value={`${metric.label} ${metric.description}`}
                  onSelect={() => {
                    onValueChange(metric.value);
                    setOpen(false);
                  }}
                  className="flex items-start gap-2 py-2"
                >
                  <Check
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      value === metric.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                  <div className="flex-1">
                    <div className="font-medium">{metric.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{metric.description}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>

            {/* Page Metrics Group */}
            <CommandGroup heading="Page Metrics">
              {PAGE_METRICS.map((metric) => (
                <CommandItem
                  key={metric.value}
                  value={`${metric.label} ${metric.description}`}
                  onSelect={() => {
                    onValueChange(metric.value);
                    setOpen(false);
                  }}
                  className="flex items-start gap-2 py-2"
                >
                  <Check
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      value === metric.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                  <div className="flex-1">
                    <div className="font-medium">{metric.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{metric.description}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Grouped and sorted metrics
const COMPANY_METRICS: Array<{ value: TileMetric; label: string; description: string }> = [
  {
    value: 'total_companies',
    label: TILE_METRIC_METADATA.total_companies.label,
    description: TILE_METRIC_METADATA.total_companies.description
  },
  {
    value: 'companies_with_multiple_pages',
    label: TILE_METRIC_METADATA.companies_with_multiple_pages.label,
    description: TILE_METRIC_METADATA.companies_with_multiple_pages.description
  },
  {
    value: 'companies_with_addons',
    label: TILE_METRIC_METADATA.companies_with_addons.label,
    description: TILE_METRIC_METADATA.companies_with_addons.description
  },
  {
    value: 'companies_with_public_pricing',
    label: TILE_METRIC_METADATA.companies_with_public_pricing.label,
    description: TILE_METRIC_METADATA.companies_with_public_pricing.description
  },
  {
    value: 'companies_with_freemium',
    label: TILE_METRIC_METADATA.companies_with_freemium.label,
    description: TILE_METRIC_METADATA.companies_with_freemium.description
  },
  {
    value: 'companies_with_trial',
    label: TILE_METRIC_METADATA.companies_with_trial.label,
    description: TILE_METRIC_METADATA.companies_with_trial.description
  },
  {
    value: 'companies_with_contact_pricing',
    label: TILE_METRIC_METADATA.companies_with_contact_pricing.label,
    description: TILE_METRIC_METADATA.companies_with_contact_pricing.description
  }
];

const PAGE_METRICS: Array<{ value: TileMetric; label: string; description: string }> = [
  {
    value: 'total_pages',
    label: TILE_METRIC_METADATA.total_pages.label,
    description: TILE_METRIC_METADATA.total_pages.description
  },
  {
    value: 'pages_with_addons',
    label: TILE_METRIC_METADATA.pages_with_addons.label,
    description: TILE_METRIC_METADATA.pages_with_addons.description
  },
  {
    value: 'avg_plans_per_page',
    label: TILE_METRIC_METADATA.avg_plans_per_page.label,
    description: TILE_METRIC_METADATA.avg_plans_per_page.description
  },
  {
    value: 'pages_with_license_pricing',
    label: TILE_METRIC_METADATA.pages_with_license_pricing.label,
    description: TILE_METRIC_METADATA.pages_with_license_pricing.description
  },
  {
    value: 'pages_with_usage_pricing',
    label: TILE_METRIC_METADATA.pages_with_usage_pricing.label,
    description: TILE_METRIC_METADATA.pages_with_usage_pricing.description
  },
  {
    value: 'pages_with_credit_pricing',
    label: TILE_METRIC_METADATA.pages_with_credit_pricing.label,
    description: TILE_METRIC_METADATA.pages_with_credit_pricing.description
  },
  {
    value: 'pages_with_thresholds',
    label: TILE_METRIC_METADATA.pages_with_thresholds.label,
    description: TILE_METRIC_METADATA.pages_with_thresholds.description
  }
];

export const TileConfigModal: React.FC<TileConfigModalProps> = ({
  open,
  onOpenChange,
  initialConfig,
  onSave,
  setDefinition,
}) => {
  const [tiles, setTiles] = useState<TileConfig[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Fetch real data for preview
  const { data: previewData } = useAnalyticsSummary(
    setDefinition && open ? setDefinitionToAnalyticsRequest(setDefinition) : null
  );

  // Initialize from config when modal opens
  useEffect(() => {
    if (!open) {
      setInitialized(false);
      return;
    }

    // Only initialize once when modal opens
    if (initialized) return;

    if (initialConfig) {
      setTiles(initialConfig.tiles);
    } else {
      // Default values
      setTiles([{ metric: 'total_companies' }]);
    }

    setInitialized(true);
  }, [open, initialConfig, initialized]);

  const handleAddTile = () => {
    if (tiles.length >= MAX_TILES) return;

    setTiles([...tiles, { metric: 'total_companies' }]);
  };

  const handleRemoveTile = (index: number) => {
    setTiles(tiles.filter((_, i) => i !== index));
  };

  const handleUpdateTileMetric = (index: number, metric: TileMetric) => {
    const newTiles = [...tiles];
    newTiles[index] = { ...newTiles[index], metric };
    setTiles(newTiles);
  };

  const handleUpdateTileLabel = (index: number, label: string) => {
    const newTiles = [...tiles];
    newTiles[index] = { ...newTiles[index], label };
    setTiles(newTiles);
  };

  const handleSave = () => {
    if (tiles.length === 0) {
      alert('Please add at least one tile');
      return;
    }

    const config: TileRowConfig = {
      tiles
    };

    onSave(config);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Tiles</DialogTitle>
          <p className="text-sm text-gray-500 mt-2">
            Period and filters are set at the report level. Configure which metrics to display here.
          </p>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Tiles Configuration */}
          <div className="space-y-4">
            <Label>Tiles ({tiles.length}/{MAX_TILES})</Label>

            {tiles.length === 0 && (
              <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                <p className="text-sm">No tiles added yet</p>
                <p className="text-xs mt-1">Click "Add Tile" to get started</p>
              </div>
            )}

            <div className="space-y-3">
              {tiles.map((tile, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div className="flex-1 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor={`tile-metric-${index}`} className="text-xs">
                        Metric
                      </Label>
                      <MetricSelector
                        id={`tile-metric-${index}`}
                        value={tile.metric}
                        onValueChange={(value) => handleUpdateTileMetric(index, value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`tile-label-${index}`} className="text-xs">
                        Custom Label (optional)
                      </Label>
                      <Input
                        id={`tile-label-${index}`}
                        value={tile.label || ''}
                        onChange={(e) => handleUpdateTileLabel(index, e.target.value)}
                        placeholder={
                          [...COMPANY_METRICS, ...PAGE_METRICS].find((m) => m.value === tile.metric)?.label ||
                          'Default label'
                        }
                        className="bg-white"
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveTile(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              {/* Add Tile button at the end */}
              {tiles.length > 0 && tiles.length < MAX_TILES && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddTile}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tile
                </Button>
              )}
            </div>

            {/* Add Tile button for empty state */}
            {tiles.length === 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddTile}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Tile
              </Button>
            )}
          </div>

          {/* Live Preview */}
          {tiles.length > 0 && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className={`grid gap-4 ${
                  tiles.length === 1
                    ? 'grid-cols-1'
                    : tiles.length === 2
                    ? 'grid-cols-2'
                    : tiles.length === 3
                    ? 'grid-cols-3'
                    : tiles.length === 4
                    ? 'grid-cols-4'
                    : 'grid-cols-5'
                }`}>
                  {tiles.map((tile, index) => {
                    const metricInfo = [...COMPANY_METRICS, ...PAGE_METRICS].find(m => m.value === tile.metric);
                    const label = tile.label || metricInfo?.label || 'Tile';

                    // Calculate real value if preview data is available
                    const { formattedCount, formattedPercentage, showPercentage } = previewData
                      ? calculateTileSummaryMetric(tile.metric, previewData)
                      : { formattedCount: '###', formattedPercentage: undefined, showPercentage: false };

                    return (
                      <div
                        key={index}
                        className="bg-white border border-gray-200 rounded-lg p-4 text-center flex flex-col justify-between min-h-[100px]"
                      >
                        <div className="text-xs font-medium text-gray-600 mb-2">{label}</div>
                        <div className="text-2xl font-bold text-gray-900">{formattedCount}</div>
                        {showPercentage && formattedPercentage && (
                          <div className="text-sm font-semibold text-gray-600 mt-1">{formattedPercentage}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Tiles</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
